import { getAllGiftcards } from "api";
import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import GiftcardModal from "./GiftcardModal";

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/Chicago",
  });
}

const SORT_OPTIONS = [
  { label: "Date — Newest", value: "date_desc" },
  { label: "Date — Oldest", value: "date_asc" },
  { label: "Amount — High", value: "amount_desc" },
  { label: "Amount — Low", value: "amount_asc" },
  { label: "Recipient A–Z", value: "recipient_asc" },
  { label: "Recipient Z–A", value: "recipient_desc" },
];

function GiftcardAll() {
  const [giftcards, setGiftcards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("date_desc");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getAllGiftcards();
        setGiftcards(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const goBack = () => {
    searchParams.delete("body");
    setSearchParams(searchParams);
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let result = q
      ? giftcards.filter((g) =>
          [
            g.recipientName,
            g.senderName,
            g.purchaserName,
            g.purchaserEmail,
            g.email,
            g.shippingAddress,
            g.message,
            String(g.amount),
          ]
            .filter(Boolean)
            .some((field) => field.toLowerCase().includes(q))
        )
      : [...giftcards];

    result.sort((a, b) => {
      switch (sort) {
        case "date_asc":
          return new Date(a.datePurchased) - new Date(b.datePurchased);
        case "date_desc":
          return new Date(b.datePurchased) - new Date(a.datePurchased);
        case "amount_asc":
          return a.amount - b.amount;
        case "amount_desc":
          return b.amount - a.amount;
        case "recipient_asc":
          return (a.recipientName || "").localeCompare(b.recipientName || "");
        case "recipient_desc":
          return (b.recipientName || "").localeCompare(a.recipientName || "");
        default:
          return 0;
      }
    });

    return result;
  }, [giftcards, search, sort]);

  return (
    <div className="gc-all-wrapper">
      <div className="gc-all-header">
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button className="nav-back-button" onClick={goBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--ad-text)", letterSpacing: "0.02em", textTransform: "none" }}>
            All Giftcards
            <span style={{ marginLeft: "10px", fontSize: "0.75rem", fontWeight: 500, color: "var(--ad-text-muted)", textTransform: "none", letterSpacing: 0 }}>
              {filtered.length} result{filtered.length !== 1 ? "s" : ""}
            </span>
          </h2>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <div className="gc-search-wrapper">
            <svg className="gc-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              className="gc-search-input"
              placeholder="Search giftcards…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="gc-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="faux-dash-item" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div className="gc-all-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="gc-all-empty">No giftcards found.</div>
        ) : (
          <table className="gc-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Recipient</th>
                <th>From</th>
                <th>Email</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => (
                <tr key={g._id} className="gc-table-row" onClick={() => setSelectedCard(g)}>
                  <td>{formatDate(g.datePurchased)}</td>
                  <td>{g.recipientName || "—"}</td>
                  <td>{g.senderName || "—"}</td>
                  <td>{g.purchaserEmail || g.email || "—"}</td>
                  <td className="gc-table-amount">${g.amount}</td>
                  <td>
                    <span className={`gc-modal-badge ${g.isPaid ? "gc-badge-paid" : "gc-badge-unpaid"}`}>
                      {g.isPaid ? "Paid" : "Unpaid"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedCard && (
        <GiftcardModal
          giftcard={selectedCard}
          onClose={() => setSelectedCard(null)}
        />
      )}
    </div>
  );
}

export default GiftcardAll;
